import type { OnDestroy, OnInit } from '@angular/core';
import { Component, HostListener, Input, ViewChild, inject } from '@angular/core';
import type { FormControl, FormGroup } from '@angular/forms';
import { NonNullableFormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import type { NgxDropzoneChangeEvent, NgxDropzoneComponent } from 'ngx-dropzone';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { Actions, ofType } from '@ngrx/effects';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BsModalRef } from 'ngx-bootstrap/modal';

import { ESavePageErrorCodes } from '@common/enums';

import { type EditPageSettings, type PageSettings } from '@common/types/page-settings.type';
import { type SitePageEntity } from '@common/types/site-page.type';

import { PageSettingsService } from '@web/store/website/page-settings/page-settings.service';
import { PageSettingsFacade } from '@web/store/website/page-settings/page-settings.facade';

import PageSettingsActions from '@web/store/website/page-settings/page-settings.actions';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { PageNameValidatorDirective } from '@web/core/validators/page-name.validator';
import { NgIf } from '@angular/common';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { PageUrlValidatorDirective } from '@web/core/validators/page-url.validator';

type EditSitePageSettingsForm = {
    name: FormControl<EditPageSettings['name']>;
    url: FormControl<EditPageSettings['url']>;
    seoName: FormControl<EditPageSettings['seoName']>;
    description: FormControl<EditPageSettings['description']>;
    keywords: FormControl<EditPageSettings['keywords']>;
    sharedImg: FormControl<EditPageSettings['sharedImg']>;
    canBeIndexed: FormControl<EditPageSettings['canBeIndexed']>;
};

type ActiveTab = 'general' | 'seo';
@Component({
    selector: 'web-edit-page-settings-modal',
    templateUrl: './edit-page-settings-modal.component.html',
    styleUrls: ['./edit-page-settings-modal.component.less'],
    imports: [
        ReactiveFormsModule,
        PageNameValidatorDirective,
        NgIf,
        TooltipModule,
        PageUrlValidatorDirective,
        NgxDropzoneModule,
        TranslateModule,
    ],
})
export class EditPageSettingsModalComponent implements OnInit, OnDestroy {
    private readonly actions$ = inject(Actions);
    private readonly fb = inject(NonNullableFormBuilder);
    private readonly bsModalRef = inject(BsModalRef);
    private readonly pageSettingsFacade = inject(PageSettingsFacade);
    private readonly pageSettingsService = inject(PageSettingsService);
    private readonly translate = inject(TranslateService);

    @Input() public siteId!: number;
    @Input() public page!: SitePageEntity;
    @Input() public pageSettings!: PageSettings;
    @Input() public siteDomain!: string;
    @Input() public disableUrl!: boolean;

    @ViewChild('drop') public dropZoneRef: NgxDropzoneComponent;

    public form: FormGroup<EditSitePageSettingsForm>;

    public readonly savePageErrorCodes = ESavePageErrorCodes;

    private readonly unsubscribe$ = new Subject<void>();
    private activeTab: ActiveTab = 'general';

    public ngOnInit(): void {
        this.actions$
            .pipe(ofType(PageSettingsActions.updatePageSettingsSuccess), takeUntil(this.unsubscribe$))
            .subscribe(() => {
                this.bsModalRef.hide();
            });

        this.initForm();

        this.setFormData();
    }

    private setFormData(): void {
        this.form.patchValue({
            name: this.page.name,
            url: this.pageSettings.url,
            seoName: this.pageSettings.seo_name,
            description: this.pageSettings.description,
            keywords: this.pageSettings.keywords,
            sharedImg: this.pageSettings.shared_img,
            canBeIndexed: this.pageSettings.can_be_indexed,
        });

        if (this.disableUrl) {
            this.url.disable();
        }
    }

    private initForm(): void {
        this.form = this.fb.group({
            name: this.fb.control(null, [Validators.required]),
            url: this.fb.control(null, [Validators.required]),
            seoName: this.fb.control(null),
            description: this.fb.control(null),
            keywords: this.fb.control(null),
            sharedImg: this.fb.control(null),
            canBeIndexed: this.fb.control(null),
        });
    }

    public changeActiveTab(tab: ActiveTab): void {
        this.activeTab = tab;
    }

    public setSharedImgFile(event: NgxDropzoneChangeEvent): void {
        this.sharedImg.setValue(event.addedFiles[0]);
        this.sharedImg.markAsDirty();
    }

    private getChangedSettings(): EditPageSettings {
        const changedSettings: EditPageSettings = {} as EditPageSettings;

        Object.keys(this.form.controls).forEach((key: string) => {
            if (this.form.controls[key].dirty) {
                changedSettings[key] = this.form.controls[key].value;
            }
        });

        return changedSettings;
    }

    public onConfirm(): void {
        if (this.disableConfirm) {
            return;
        }

        this.form.disable({ emitEvent: false });

        this.pageSettingsFacade.updatePageSettings(
            Number(this.siteId),
            Number(this.page.id),
            this.pageSettingsService.mapEditPageSettingsToEditPageSettingsRequest(this.getChangedSettings())
        );
    }

    public onCancel(): void {
        this.bsModalRef.hide();
    }

    @HostListener('document:keydown.enter', ['$event'])
    public enterKeyEvent(event: KeyboardEvent): void {
        event.stopPropagation();

        if (this.disableConfirm) {
            return;
        }

        this.onConfirm();
    }

    public get isGeneralTabActive(): boolean {
        return this.activeTab === 'general';
    }

    public get isSeoTabActive(): boolean {
        return this.activeTab === 'seo';
    }

    public get sharedImgName(): string {
        if (this.sharedImg.value instanceof File) {
            return this.sharedImg.value?.name || '';
        }

        return this.pageSettings.shared_img_name;
    }

    public get isSharedImgFile(): boolean {
        return this.sharedImg.value instanceof File;
    }

    public get disableConfirm(): boolean {
        return this.form.invalid || this.form.disabled || this.form.pending || this.form.pristine;
    }

    public get name(): EditSitePageSettingsForm['name'] {
        return this.form.get('name') as EditSitePageSettingsForm['name'];
    }

    public get url(): EditSitePageSettingsForm['url'] {
        return this.form.get('url') as EditSitePageSettingsForm['url'];
    }

    public get description(): EditSitePageSettingsForm['description'] {
        return this.form.get('description') as EditSitePageSettingsForm['description'];
    }

    public get seoName(): EditSitePageSettingsForm['seoName'] {
        return this.form.get('seoName') as EditSitePageSettingsForm['seoName'];
    }

    public get keywords(): EditSitePageSettingsForm['keywords'] {
        return this.form.get('keywords') as EditSitePageSettingsForm['keywords'];
    }

    public get sharedImg(): EditSitePageSettingsForm['sharedImg'] {
        return this.form.get('sharedImg') as EditSitePageSettingsForm['sharedImg'];
    }

    public get canBeIndexed(): EditSitePageSettingsForm['canBeIndexed'] {
        return this.form.get('canBeIndexed') as EditSitePageSettingsForm['canBeIndexed'];
    }

    public toogleCanBeIndexed() {
        this.canBeIndexed.setValue(+!this.canBeIndexed.value);
        this.canBeIndexed.markAsDirty();
    }

    public get isCartPage(): boolean {
        return this.page?.url?.split('/')?.at(-1) === 'cart';
    }

    public get urlTooltipText(): string {
        return this.isCartPage ? this.translate.instant('cart_url_settings_noty') : null;
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
